// Enhanced search utilities for the Explore page
import exercisesData from '../data/exercises.json';

// Query expansion mappings
const queryExpansions = {
  // Muscle group expansions
  'chest': ['chest', 'pecs', 'pectorals', 'chest muscles', 'upper chest', 'lower chest'],
  'back': ['back', 'lats', 'latissimus', 'rhomboids', 'traps', 'trapezius', 'upper back', 'lower back'],
  'shoulders': ['shoulders', 'delts', 'deltoids', 'shoulder muscles', 'lateral delts', 'rear delts', 'front delts'],
  'arms': ['arms', 'biceps', 'triceps', 'forearms', 'arm muscles'],
  'biceps': ['biceps', 'bicep', 'arm muscles', 'bicep curls'],
  'triceps': ['triceps', 'tricep', 'arm muscles', 'tricep extensions'],
  'legs': ['legs', 'quads', 'quadriceps', 'hamstrings', 'calves', 'thighs', 'leg muscles'],
  'quads': ['quads', 'quadriceps', 'thighs', 'front legs', 'leg muscles'],
  'hamstrings': ['hamstrings', 'hams', 'back legs', 'leg muscles'],
  'calves': ['calves', 'calf muscles', 'lower legs'],
  'glutes': ['glutes', 'glute muscles', 'butt', 'buttocks', 'hip muscles'],
  'core': ['core', 'abs', 'abdominals', 'stomach', 'belly', 'six pack', 'ab muscles'],
  'abs': ['abs', 'abdominals', 'core', 'stomach', 'belly', 'six pack'],
  
  // Exercise type expansions
  'push': ['push', 'pressing', 'pushing', 'press'],
  'pull': ['pull', 'pulling', 'rowing', 'pulldown'],
  'squat': ['squat', 'squats', 'squatting', 'leg press'],
  'lunge': ['lunge', 'lunges', 'lunging', 'step'],
  'plank': ['plank', 'planks', 'planking', 'core hold'],
  'curl': ['curl', 'curls', 'curling', 'bicep curl'],
  'press': ['press', 'pressing', 'overhead press', 'bench press'],
  'row': ['row', 'rowing', 'pulldown', 'lat pulldown'],
  'deadlift': ['deadlift', 'deadlifts', 'hip hinge'],
  
  // Equipment expansions
  'dumbbell': ['dumbbell', 'dumbbells', 'db', 'free weights'],
  'barbell': ['barbell', 'barbells', 'bb', 'bar'],
  'cable': ['cable', 'cables', 'machine'],
  'bodyweight': ['bodyweight', 'body weight', 'no equipment', 'body only', 'calisthenics'],
  'kettlebell': ['kettlebell', 'kettlebells', 'kb'],
  'resistance': ['resistance', 'band', 'bands', 'resistance band'],
  
  // Movement pattern expansions
  'compound': ['compound', 'multi-joint', 'full body'],
  'isolation': ['isolation', 'single joint', 'targeted'],
  'cardio': ['cardio', 'cardiovascular', 'endurance', 'aerobic'],
  'strength': ['strength', 'power', 'heavy', 'max strength'],
  'endurance': ['endurance', 'stamina', 'long duration'],
  'flexibility': ['flexibility', 'stretching', 'mobility', 'range of motion'],
  
  // Common misspellings and variations
  'pushup': ['pushup', 'push-up', 'push ups', 'pushups'],
  'pullup': ['pullup', 'pull-up', 'pull ups', 'pullups'],
  'situp': ['situp', 'sit-up', 'sit ups', 'situps'],
  'burpee': ['burpee', 'burpees', 'burpy'],
  'plank': ['plank', 'planks', 'planking'],
};

// Semantic relationships between terms
const semanticRelations = {
  'chest': ['push', 'pressing', 'bench', 'pecs'],
  'back': ['pull', 'rowing', 'lats', 'rhomboids'],
  'shoulders': ['press', 'overhead', 'delts', 'lateral'],
  'legs': ['squat', 'lunge', 'step', 'leg press'],
  'arms': ['curl', 'extension', 'bicep', 'tricep'],
  'core': ['plank', 'crunch', 'situp', 'ab'],
  'glutes': ['squat', 'hip thrust', 'bridge', 'butt'],
  'cardio': ['jump', 'run', 'bike', 'endurance'],
  'strength': ['heavy', 'power', 'max', 'strong'],
  'flexibility': ['stretch', 'yoga', 'mobility', 'range'],
};

// Fuzzy matching function using Levenshtein distance
function levenshteinDistance(str1, str2) {
  const matrix = [];
  const len1 = str1.length;
  const len2 = str2.length;

  for (let i = 0; i <= len2; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len1; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len2; i++) {
    for (let j = 1; j <= len1; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[len2][len1];
}

// Calculate fuzzy match score (0-1, where 1 is perfect match)
function getFuzzyScore(query, target) {
  const distance = levenshteinDistance(query.toLowerCase(), target.toLowerCase());
  const maxLength = Math.max(query.length, target.length);
  return maxLength === 0 ? 1 : 1 - (distance / maxLength);
}

// Expand query with related terms
function expandQuery(query) {
  const terms = query.toLowerCase().split(/\s+/);
  const expandedTerms = new Set(terms);

  terms.forEach(term => {
    // Add direct expansions
    if (queryExpansions[term]) {
      queryExpansions[term].forEach(expansion => expandedTerms.add(expansion));
    }
    
    // Add semantic relationships
    if (semanticRelations[term]) {
      semanticRelations[term].forEach(related => expandedTerms.add(related));
    }
  });

  return Array.from(expandedTerms);
}

// Enhanced search function
export function enhancedSearch(exercises, query) {
  if (!query || query.trim() === '') {
    return exercises;
  }

  const expandedTerms = expandQuery(query);
  const results = [];

  exercises.forEach(exercise => {
    let score = 0;
    const searchableText = [
      exercise.name,
      ...(exercise.primaryMuscles || []),
      ...(exercise.secondaryMuscles || []),
      exercise.category,
      exercise.equipment,
      exercise.level,
      ...(exercise.instructions || [])
    ].join(' ').toLowerCase();

    // Exact matches get highest score
    if (searchableText.includes(query.toLowerCase())) {
      score += 10;
    }

    // Check each expanded term
    expandedTerms.forEach(term => {
      // Exact term match
      if (searchableText.includes(term)) {
        score += 5;
      }
      
      // Fuzzy matching for close matches
      const fuzzyScore = getFuzzyScore(term, exercise.name);
      if (fuzzyScore > 0.7) {
        score += fuzzyScore * 3;
      }
      
      // Check fuzzy matching against other fields
      exercise.primaryMuscles?.forEach(muscle => {
        const muscleScore = getFuzzyScore(term, muscle);
        if (muscleScore > 0.6) {
          score += muscleScore * 2;
        }
      });
    });

    // Boost score for exact name matches
    const nameFuzzyScore = getFuzzyScore(query, exercise.name);
    if (nameFuzzyScore > 0.5) {
      score += nameFuzzyScore * 5;
    }

    if (score > 0) {
      results.push({ exercise, score });
    }
  });

  // Sort by score (highest first) and return exercises
  return results
    .sort((a, b) => b.score - a.score)
    .map(result => result.exercise);
}

// Get search suggestions based on query
export function getSearchSuggestions(query, limit = 5) {
  if (!query || query.length < 2) return [];

  const suggestions = new Set();
  const expandedTerms = expandQuery(query);

  // Add matching exercise names
  exercisesData.forEach(exercise => {
    const nameFuzzyScore = getFuzzyScore(query, exercise.name);
    if (nameFuzzyScore > 0.6) {
      suggestions.add(exercise.name);
    }
  });

  // Add matching muscle groups
  Object.keys(queryExpansions).forEach(muscle => {
    const fuzzyScore = getFuzzyScore(query, muscle);
    if (fuzzyScore > 0.6) {
      suggestions.add(muscle);
    }
  });

  // Add matching equipment
  const equipment = [...new Set(exercisesData.map(ex => ex.equipment).filter(Boolean))];
  equipment.forEach(eq => {
    const fuzzyScore = getFuzzyScore(query, eq);
    if (fuzzyScore > 0.6) {
      suggestions.add(eq);
    }
  });

  return Array.from(suggestions).slice(0, limit);
}

// Get trending/popular search terms
export function getTrendingTerms() {
  const muscleGroups = ['chest', 'back', 'shoulders', 'legs', 'arms', 'core', 'glutes'];
  const exerciseTypes = ['push', 'pull', 'squat', 'lunge', 'plank', 'curl'];
  const equipment = ['dumbbell', 'barbell', 'bodyweight', 'cable', 'kettlebell'];
  
  return [...muscleGroups, ...exerciseTypes, ...equipment];
}

export default {
  enhancedSearch,
  getSearchSuggestions,
  getTrendingTerms,
  expandQuery,
  getFuzzyScore
};

